/**
 * Procedural sprite art.
 *
 * Every celestial body is drawn once into an offscreen canvas and cached by
 * `(class, seed, size)`, then blitted. That matters: 61 hexes × per-frame gradient work
 * would burn the frame budget, whereas 61 `drawImage` calls cost almost nothing.
 *
 * All bodies share one lighting convention — **light from the upper-left** — with a
 * terminator shadow and a rim light on the dark limb. Consistent lighting is most of what
 * makes a set of procedural planets read as one coherent scene rather than a sticker sheet.
 */

import { SYSTEM_CLASSES } from '../game/constants';
import type {
  MegastructureKind, PlayerId, RelicKind, Resources, StarSystem, SystemClass,
} from '../game/types';
import {
  INK, RESOURCE_GLYPH, RESOURCE_INK, SEAT_SHAPE, contrastText, mixColor, seatColor, shade,
  systemPaint, withAlpha,
} from './palette';

/* ------------------------------------------------------------------- plumbing */

/**
 * Local mulberry32. Deliberately not imported from `game/rng`: the render layer stays
 * independent of the engine, and art randomness must never touch the game's PRNG cursor.
 */
function prng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type Surface = { canvas: CanvasImageSource; ctx: CanvasRenderingContext2D };

/**
 * Allocate an offscreen drawing surface.
 * `OffscreenCanvas` where available; a detached `<canvas>` on Safari before 16.4.
 */
function surface(width: number, height: number): Surface | null {
  const w = Math.max(1, Math.ceil(width));
  const h = Math.max(1, Math.ceil(height));

  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
    if (ctx) return { canvas, ctx };
  }
  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  return ctx ? { canvas, ctx } : null;
}

/* ---------------------------------------------------------------------- cache */

export interface SpriteCache {
  clear(): void;
  readonly size: number;
}

const cache = new Map<string, CanvasImageSource>();

/**
 * Bounded LRU-ish cache. The board only ever needs a handful of distinct sizes (the
 * renderer quantises to multiples of 4), so this rarely evicts — the cap exists so a
 * pathological zoom animation cannot leak canvases.
 */
const CACHE_LIMIT = 420;

function cached(key: string, build: () => CanvasImageSource | null): CanvasImageSource {
  const hit = cache.get(key);
  if (hit) {
    // Refresh insertion order so the oldest genuinely-unused entry is evicted first.
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  const made = build();
  if (!made) return FALLBACK;

  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, made);
  return made;
}

/** 1×1 transparent stand-in for environments with no canvas at all (SSR, tests). */
const FALLBACK: CanvasImageSource = (() => {
  const s = surface(1, 1);
  return s ? s.canvas : ({} as CanvasImageSource);
})();

export const spriteCache: SpriteCache = {
  clear: () => cache.clear(),
  get size() {
    return cache.size;
  },
};

/* ------------------------------------------------------------------- geometry */

/** Pointy-top hex path. Does not fill or stroke — callers decide. */
export function hexPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 90);
    const x = cx + size * Math.cos(angle);
    const y = cy + size * Math.sin(angle);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/* ------------------------------------------------------------- planet lighting */

/** Light direction, normalised. Upper-left, shared by every body. */
const LIGHT = { x: -0.58, y: -0.62 };

/**
 * Terminator + rim light, applied over an already-painted body.
 * Clipped to the disc by the caller.
 */
function applyLighting(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rimColor: string,
): void {
  // Shadow side: a radial gradient offset away from the light.
  const shadow = ctx.createRadialGradient(
    cx + LIGHT.x * r * 0.55, cy + LIGHT.y * r * 0.55, r * 0.14,
    cx - LIGHT.x * r * 0.75, cy - LIGHT.y * r * 0.75, r * 1.5,
  );
  shadow.addColorStop(0, 'rgba(0,0,0,0)');
  shadow.addColorStop(0.52, 'rgba(0,0,0,0.28)');
  shadow.addColorStop(1, 'rgba(0,0,0,0.86)');
  ctx.fillStyle = shadow;
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);

  // Specular bloom on the lit limb.
  const bloom = ctx.createRadialGradient(
    cx + LIGHT.x * r * 0.5, cy + LIGHT.y * r * 0.5, 0,
    cx + LIGHT.x * r * 0.5, cy + LIGHT.y * r * 0.5, r * 0.95,
  );
  bloom.addColorStop(0, 'rgba(255,255,255,0.2)');
  bloom.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = bloom;
  ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  ctx.restore();

  // Rim light on the dark limb — the cue that sells "sphere in space".
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineWidth = Math.max(1, r * 0.09);
  ctx.strokeStyle = withAlpha(rimColor, 0.5);
  ctx.beginPath();
  const start = Math.atan2(-LIGHT.y, -LIGHT.x) - 1.15;
  ctx.arc(cx, cy, r - ctx.lineWidth * 0.42, start, start + 2.3);
  ctx.stroke();
  ctx.restore();
}

/** Soft value-noise blob, used for continents and storms. */
function blob(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
  rand: () => number,
  color: string,
): void {
  const points = 9 + Math.floor(rand() * 4);
  ctx.beginPath();
  for (let i = 0; i <= points; i++) {
    const angle = (i / points) * Math.PI * 2;
    // Two-octave wobble keeps the outline organic rather than star-shaped.
    const wobble = 0.62 + rand() * 0.5 + Math.sin(angle * 3 + rand()) * 0.12;
    const rr = radius * wobble;
    const x = cx + Math.cos(angle) * rr;
    const y = cy + Math.sin(angle) * rr * 0.82;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

/* ------------------------------------------------------------- planet painters */

type Painter = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rand: () => number,
  cls: SystemClass,
) => void;

const PAINTERS: Partial<Record<SystemClass, Painter>> = {
  terran(ctx, cx, cy, r, rand) {
    const paint = systemPaint('terran');
    // Ocean base.
    ctx.fillStyle = '#1d4f7a';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    // Continents in two value layers, so landmasses have interior variation.
    for (let i = 0; i < 4; i++) {
      blob(
        ctx,
        cx + (rand() - 0.5) * r * 1.15,
        cy + (rand() - 0.5) * r * 1.15,
        r * (0.3 + rand() * 0.34),
        rand,
        withAlpha(paint.body, 0.95),
      );
    }
    for (let i = 0; i < 3; i++) {
      blob(
        ctx,
        cx + (rand() - 0.5) * r * 1.1,
        cy + (rand() - 0.5) * r * 1.1,
        r * (0.16 + rand() * 0.2),
        rand,
        withAlpha(paint.accent, 0.5),
      );
    }

    // Polar caps.
    for (const sign of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(cx, cy + sign * r * 0.92, r * 0.62, r * 0.24, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(232, 244, 255, 0.72)';
      ctx.fill();
    }

    // Thin cloud veil.
    ctx.save();
    ctx.globalAlpha = 0.2;
    for (let i = 0; i < 3; i++) {
      blob(ctx, cx + (rand() - 0.5) * r, cy + (rand() - 0.5) * r, r * 0.4, rand, '#ffffff');
    }
    ctx.restore();
  },

  oceanic(ctx, cx, cy, r, rand) {
    const grad = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r);
    grad.addColorStop(0, '#4ba3d8');
    grad.addColorStop(0.6, '#2f7fb8');
    grad.addColorStop(1, '#134a72');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    // Swirling cloud bands: sine-warped horizontal strokes.
    ctx.save();
    ctx.globalAlpha = 0.42;
    ctx.strokeStyle = '#e8f6ff';
    ctx.lineWidth = Math.max(1, r * 0.1);
    ctx.lineCap = 'round';
    for (let i = 0; i < 5; i++) {
      const y = cy + (rand() - 0.5) * r * 1.5;
      const phase = rand() * Math.PI * 2;
      ctx.beginPath();
      for (let x = -r; x <= r; x += r * 0.14) {
        const yy = y + Math.sin(x / r * 2.4 + phase) * r * 0.1;
        if (x === -r) ctx.moveTo(cx + x, yy);
        else ctx.lineTo(cx + x, yy);
      }
      ctx.stroke();
    }
    ctx.restore();

    // Sun glint on the lit side — the giveaway that it is a water world.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const glint = ctx.createRadialGradient(
      cx + LIGHT.x * r * 0.45, cy + LIGHT.y * r * 0.45, 0,
      cx + LIGHT.x * r * 0.45, cy + LIGHT.y * r * 0.45, r * 0.4,
    );
    glint.addColorStop(0, 'rgba(255,255,255,0.65)');
    glint.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = glint;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.restore();
  },

  ice(ctx, cx, cy, r, rand) {
    const grad = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.35, r * 0.1, cx, cy, r);
    grad.addColorStop(0, '#f2fbff');
    grad.addColorStop(0.55, '#b9d6e6');
    grad.addColorStop(1, '#7796a8');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    // Fracture lines: brighter than the surface, branching once.
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.72)';
    ctx.lineWidth = Math.max(0.7, r * 0.035);
    for (let i = 0; i < 6; i++) {
      let x = cx + (rand() - 0.5) * r * 1.4;
      let y = cy + (rand() - 0.5) * r * 1.4;
      let angle = rand() * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let step = 0; step < 4; step++) {
        angle += (rand() - 0.5) * 1.1;
        x += Math.cos(angle) * r * 0.22;
        y += Math.sin(angle) * r * 0.22;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  },

  desert(ctx, cx, cy, r, rand) {
    const grad = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
    grad.addColorStop(0, '#e6c58c');
    grad.addColorStop(0.5, '#c99a54');
    grad.addColorStop(1, '#8f6a34');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    // Dune bands.
    ctx.save();
    ctx.globalAlpha = 0.3;
    for (let i = 0; i < 7; i++) {
      const y = cy - r + (i / 7) * r * 2 + rand() * r * 0.1;
      ctx.fillStyle = i % 2 === 0 ? '#f2d9a8' : '#7d5a2c';
      ctx.beginPath();
      ctx.ellipse(cx, y, r * 0.98, r * 0.09, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // A couple of craters, lit consistently with LIGHT.
    for (let i = 0; i < 2; i++) {
      const cr = r * (0.14 + rand() * 0.1);
      const x = cx + (rand() - 0.5) * r * 1.1;
      const y = cy + (rand() - 0.5) * r * 1.1;
      ctx.beginPath();
      ctx.arc(x, y, cr, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(90, 64, 30, 0.5)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x - cr * 0.2, y - cr * 0.2, cr * 0.8, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(242, 217, 168, 0.28)';
      ctx.fill();
    }
  },

  volcanic(ctx, cx, cy, r, rand) {
    ctx.fillStyle = '#2b1512';
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    // Dark crust plates.
    for (let i = 0; i < 5; i++) {
      blob(
        ctx,
        cx + (rand() - 0.5) * r * 1.2,
        cy + (rand() - 0.5) * r * 1.2,
        r * (0.24 + rand() * 0.28),
        rand,
        'rgba(58, 30, 24, 0.9)',
      );
    }

    // Magma fissures, additive so they genuinely glow.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 7; i++) {
      let x = cx + (rand() - 0.5) * r * 1.3;
      let y = cy + (rand() - 0.5) * r * 1.3;
      let angle = rand() * Math.PI * 2;
      ctx.strokeStyle = i % 2 === 0 ? 'rgba(255, 138, 60, 0.9)' : 'rgba(255, 200, 90, 0.75)';
      ctx.lineWidth = Math.max(0.8, r * (0.03 + rand() * 0.04));
      ctx.shadowColor = 'rgba(255, 120, 40, 0.9)';
      ctx.shadowBlur = r * 0.28;
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let step = 0; step < 3; step++) {
        angle += (rand() - 0.5) * 1.3;
        x += Math.cos(angle) * r * 0.24;
        y += Math.sin(angle) * r * 0.24;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  },

  gasGiant(ctx, cx, cy, r, rand) {
    const paint = systemPaint('gasGiant');
    ctx.fillStyle = paint.body;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    // Turbulent bands: offset sine-warped horizontal stripes of alternating value.
    const bands = 9;
    for (let i = 0; i < bands; i++) {
      const t = i / bands;
      const y = cy - r + t * r * 2;
      const thickness = (r * 2 / bands) * (0.7 + rand() * 0.7);
      const tone = i % 2 === 0
        ? mixColor(paint.body, paint.accent, 0.35 + rand() * 0.4)
        : shade(paint.body, -0.28 - rand() * 0.2);

      ctx.save();
      ctx.globalAlpha = 0.85;
      ctx.fillStyle = tone;
      ctx.beginPath();
      const phase = rand() * Math.PI * 2;
      ctx.moveTo(cx - r, y);
      for (let x = -r; x <= r; x += r * 0.1) {
        ctx.lineTo(cx + x, y + Math.sin(x / r * 3 + phase) * thickness * 0.22);
      }
      for (let x = r; x >= -r; x -= r * 0.1) {
        ctx.lineTo(cx + x, y + thickness + Math.sin(x / r * 3 + phase + 1) * thickness * 0.22);
      }
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // The great oval storm.
    const sx = cx + (rand() - 0.5) * r * 0.7;
    const sy = cy + (rand() - 0.5) * r * 0.8;
    ctx.save();
    ctx.translate(sx, sy);
    ctx.rotate((rand() - 0.5) * 0.5);
    const storm = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.3);
    storm.addColorStop(0, 'rgba(255, 236, 214, 0.9)');
    storm.addColorStop(0.5, withAlpha(paint.accent, 0.7));
    storm.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = storm;
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 0.3, r * 0.17, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  },

  asteroid(ctx, cx, cy, r, rand) {
    // Not a sphere: a cluster of irregular lit rocks with shadowed sides.
    const count = 8 + Math.floor(rand() * 7);
    for (let i = 0; i < count; i++) {
      const angle = rand() * Math.PI * 2;
      const dist = rand() * r * 0.82;
      const x = cx + Math.cos(angle) * dist;
      const y = cy + Math.sin(angle) * dist * 0.78;
      const rr = r * (0.1 + rand() * 0.19);

      // Irregular silhouette.
      ctx.beginPath();
      const sides = 6 + Math.floor(rand() * 3);
      for (let s = 0; s <= sides; s++) {
        const a = (s / sides) * Math.PI * 2;
        const rad = rr * (0.68 + rand() * 0.5);
        const px = x + Math.cos(a) * rad;
        const py = y + Math.sin(a) * rad;
        if (s === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = '#8e8a97';
      ctx.fill();

      // Lit cap toward the light, shadow away from it.
      ctx.save();
      ctx.clip();
      const lit = ctx.createLinearGradient(
        x + LIGHT.x * rr, y + LIGHT.y * rr,
        x - LIGHT.x * rr, y - LIGHT.y * rr,
      );
      lit.addColorStop(0, 'rgba(216, 213, 224, 0.95)');
      lit.addColorStop(0.5, 'rgba(125, 122, 134, 0.5)');
      lit.addColorStop(1, 'rgba(28, 26, 36, 0.85)');
      ctx.fillStyle = lit;
      ctx.fillRect(x - rr * 1.4, y - rr * 1.4, rr * 2.8, rr * 2.8);
      ctx.restore();
    }
  },

  anomaly(ctx, cx, cy, r, rand) {
    // Lensing dark core.
    const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 0.62);
    core.addColorStop(0, '#000006');
    core.addColorStop(0.72, '#120c33');
    core.addColorStop(1, 'rgba(24, 16, 62, 0)');
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // Accretion ring, seen at an angle.
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(-0.42);
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.ellipse(0, 0, r * (0.72 + i * 0.1), r * (0.2 + i * 0.05), 0, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${150 + i * 30}, ${120 + i * 20}, 255, ${0.5 - i * 0.13})`;
      ctx.lineWidth = Math.max(0.8, r * (0.07 - i * 0.017));
      ctx.shadowColor = 'rgba(160, 130, 255, 0.9)';
      ctx.shadowBlur = r * 0.34;
      ctx.stroke();
    }
    ctx.restore();

    // Radial caustics — light bent around the core.
    const spokes = 7;
    for (let i = 0; i < spokes; i++) {
      const angle = (i / spokes) * Math.PI * 2 + rand() * 0.3;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(angle) * r * 0.6, cy + Math.sin(angle) * r * 0.6);
      ctx.lineTo(cx + Math.cos(angle) * r * 1.0, cy + Math.sin(angle) * r * 1.0);
      ctx.strokeStyle = 'rgba(180, 166, 255, 0.28)';
      ctx.lineWidth = Math.max(0.6, r * 0.035);
      ctx.stroke();
    }
    ctx.restore();
  },
};

/** Faint dust wisp for empty hexes. */
function paintDeepSpace(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rand: () => number,
): void {
  ctx.save();
  ctx.globalAlpha = 0.24;
  for (let i = 0; i < 2; i++) {
    const grad = ctx.createRadialGradient(
      cx + (rand() - 0.5) * r, cy + (rand() - 0.5) * r, 0,
      cx, cy, r * 0.9,
    );
    grad.addColorStop(0, 'rgba(90, 104, 150, 0.5)');
    grad.addColorStop(1, 'rgba(90, 104, 150, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  }
  ctx.restore();
}

/* ------------------------------------------------------------ public: bodies */

/**
 * Planet art for a system, cached.
 * `diameterPx` should be quantised by the caller so the cache does not thrash.
 */
export function planetSprite(system: StarSystem, diameterPx: number): CanvasImageSource {
  const d = Math.max(8, Math.round(diameterPx));
  const key = `p:${system.cls}:${system.artSeed}:${d}`;

  return cached(key, () => {
    // Pad so glows and rim lights are not clipped at the sprite edge.
    const pad = Math.ceil(d * 0.16);
    const size = d + pad * 2;
    const s = surface(size, size);
    if (!s) return null;

    const { ctx } = s;
    const cx = size / 2;
    const cy = size / 2;
    // Bodies sit a little inside the hex so the plate rim stays visible.
    const r = d * 0.39;
    const rand = prng(system.artSeed || 1);

    if (system.cls === 'deepSpace') {
      paintDeepSpace(ctx, cx, cy, r * 1.5, rand);
      return s.canvas;
    }

    const paint = systemPaint(system.cls);

    // Outer atmosphere/corona glow.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const halo = ctx.createRadialGradient(cx, cy, r * 0.9, cx, cy, r * 1.42);
    halo.addColorStop(0, withAlpha(paint.accent, 0.3));
    halo.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = halo;
    ctx.fillRect(0, 0, size, size);
    ctx.restore();

    // Body, clipped to the disc.
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.clip();

    const painter = PAINTERS[system.cls];
    if (painter) painter(ctx, cx, cy, r, rand, system.cls);
    else {
      ctx.fillStyle = paint.body;
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    }

    // Asteroid fields and anomalies do their own lighting; spheres get the shared pass.
    if (system.cls !== 'asteroid' && system.cls !== 'anomaly') {
      applyLighting(ctx, cx, cy, r, paint.accent);
    }
    ctx.restore();

    // Crisp terminator edge on the disc itself.
    if (system.cls !== 'asteroid' && system.cls !== 'anomaly') {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = withAlpha(shade(paint.body, -0.6), 0.6);
      ctx.lineWidth = Math.max(0.6, r * 0.045);
      ctx.stroke();
      ctx.restore();
    }

    return s.canvas;
  });
}

/** The unsurveyed glyph: a sensor return with no resolution. */
export function unknownSprite(seed: number, diameterPx: number): CanvasImageSource {
  const d = Math.max(8, Math.round(diameterPx));
  const key = `u:${seed}:${d}`;

  return cached(key, () => {
    const s = surface(d, d);
    if (!s) return null;
    const { ctx } = s;
    const cx = d / 2;
    const cy = d / 2;
    const r = d * 0.3;
    const rand = prng(seed || 1);

    // A dashed sensor circle plus a scatter of unresolved returns.
    ctx.save();
    ctx.strokeStyle = withAlpha(INK.unknown, 0.9);
    ctx.lineWidth = Math.max(1, r * 0.12);
    ctx.setLineDash([r * 0.42, r * 0.34]);
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = withAlpha(INK.textFaint, 0.55);
    for (let i = 0; i < 5; i++) {
      const angle = rand() * Math.PI * 2;
      const dist = rand() * r * 0.62;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(angle) * dist, cy + Math.sin(angle) * dist, Math.max(0.7, r * 0.08), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // A soft "?" built from an arc and a dot, so no font is involved.
    ctx.save();
    ctx.strokeStyle = withAlpha(INK.textDim, 0.75);
    ctx.lineWidth = Math.max(1.1, r * 0.17);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(cx, cy - r * 0.18, r * 0.3, Math.PI * 0.92, Math.PI * 2.28);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx + r * 0.06, cy + r * 0.1);
    ctx.lineTo(cx + r * 0.06, cy + r * 0.3);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx + r * 0.06, cy + r * 0.56, Math.max(0.9, r * 0.1), 0, Math.PI * 2);
    ctx.fillStyle = withAlpha(INK.textDim, 0.8);
    ctx.fill();
    ctx.restore();

    return s.canvas;
  });
}

/* ------------------------------------------------------------ public: pieces */

/**
 * A fleet chevron. Drawn live rather than cached — there are at most ~20 on screen and
 * they carry a rotation and a count badge, which would multiply cache keys for nothing.
 */
export function drawFleet(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  seat: PlayerId,
  opts: { count?: number; facing?: number; ghost?: boolean } = {},
): void {
  const { count = 1, facing = 0, ghost = false } = opts;
  const color = seatColor(seat);

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(facing);
  if (ghost) ctx.globalAlpha = 0.42;

  // Hull: a swept chevron with a notched tail.
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.55, size * 0.66);
  ctx.lineTo(-size * 0.2, 0);
  ctx.lineTo(-size * 0.55, -size * 0.66);
  ctx.closePath();

  const grad = ctx.createLinearGradient(-size * 0.5, 0, size, 0);
  grad.addColorStop(0, shade(color, -0.45));
  grad.addColorStop(1, color);
  ctx.fillStyle = grad;
  ctx.shadowColor = withAlpha(color, 0.8);
  ctx.shadowBlur = size * 0.55;
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.strokeStyle = shade(color, 0.45);
  ctx.lineWidth = Math.max(0.7, size * 0.09);
  ctx.stroke();

  // Engine glow at the tail.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const wash = ctx.createRadialGradient(-size * 0.42, 0, 0, -size * 0.42, 0, size * 0.5);
  wash.addColorStop(0, withAlpha(color, 0.85));
  wash.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = wash;
  ctx.fillRect(-size, -size * 0.6, size, size * 1.2);
  ctx.restore();
  ctx.restore();

  // Count badge, upright regardless of the chevron's rotation.
  if (count > 1) {
    const bx = x + size * 0.72;
    const by = y - size * 0.72;
    const br = size * 0.46;
    ctx.save();
    ctx.beginPath();
    ctx.arc(bx, by, br, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = Math.max(0.6, br * 0.16);
    ctx.stroke();

    ctx.fillStyle = contrastText(color);
    ctx.font = `800 ${br * 1.35}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(count), bx, by + br * 0.06);
    ctx.restore();
  }
}

/** Outpost struts or a domed colony, in seat colour. */
export function drawDevelopment(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  seat: PlayerId,
  development: 'outpost' | 'colony',
): void {
  const color = seatColor(seat);
  ctx.save();
  ctx.translate(x, y);

  if (development === 'outpost') {
    // A small strut cluster with an antenna — reads as "a foothold".
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1.2, size * 0.14);
    ctx.lineCap = 'round';
    ctx.shadowColor = withAlpha(color, 0.7);
    ctx.shadowBlur = size * 0.5;

    ctx.beginPath();
    ctx.moveTo(-size * 0.5, size * 0.42);
    ctx.lineTo(0, -size * 0.1);
    ctx.lineTo(size * 0.5, size * 0.42);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, -size * 0.1);
    ctx.lineTo(0, -size * 0.72);
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(0, -size * 0.8, size * 0.15, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  } else {
    // Domed city: a lit hemisphere with window rows and a ground glow.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 1.3);
    glow.addColorStop(0, withAlpha(color, 0.42));
    glow.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(-size * 1.4, -size * 1.4, size * 2.8, size * 2.8);
    ctx.restore();

    ctx.beginPath();
    ctx.arc(0, size * 0.28, size * 0.72, Math.PI, Math.PI * 2);
    ctx.closePath();
    const dome = ctx.createLinearGradient(0, -size * 0.5, 0, size * 0.3);
    dome.addColorStop(0, shade(color, 0.5));
    dome.addColorStop(1, shade(color, -0.3));
    ctx.fillStyle = dome;
    ctx.fill();
    ctx.strokeStyle = shade(color, 0.6);
    ctx.lineWidth = Math.max(0.8, size * 0.09);
    ctx.stroke();

    // Base platform.
    ctx.fillStyle = shade(color, -0.5);
    ctx.fillRect(-size * 0.78, size * 0.28, size * 1.56, size * 0.2);

    // Lit windows.
    ctx.fillStyle = 'rgba(255, 246, 214, 0.92)';
    for (let row = 0; row < 2; row++) {
      const wy = size * (0.02 + row * 0.16);
      const span = size * (0.5 - row * 0.16);
      for (let i = -2; i <= 2; i++) {
        const wx = (i / 2) * span;
        ctx.fillRect(wx - size * 0.055, wy, size * 0.11, size * 0.1);
      }
    }
  }
  ctx.restore();
}

/** Relic sigil — a distinct mark per kind, dimmed once recovered. */
export function drawRelic(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  relic: RelicKind,
  taken: boolean,
): void {
  const color = taken ? INK.textFaint : '#ffd76b';
  ctx.save();
  ctx.translate(x, y);
  if (taken) ctx.globalAlpha = 0.4;

  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1, size * 0.16);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (!taken) {
    ctx.shadowColor = withAlpha(color, 0.9);
    ctx.shadowBlur = size * 0.9;
  }

  switch (relic) {
    case 'cache':
      // A sealed crate.
      ctx.strokeRect(-size * 0.62, -size * 0.5, size * 1.24, size);
      ctx.beginPath();
      ctx.moveTo(-size * 0.62, -size * 0.1);
      ctx.lineTo(size * 0.62, -size * 0.1);
      ctx.stroke();
      break;

    case 'archive':
      // Stacked data plates.
      for (let i = 0; i < 3; i++) {
        ctx.strokeRect(-size * 0.6, -size * 0.6 + i * size * 0.42, size * 1.2, size * 0.28);
      }
      break;

    case 'foundry':
      // An anvil-ish wedge with a spark.
      ctx.beginPath();
      ctx.moveTo(-size * 0.66, size * 0.42);
      ctx.lineTo(size * 0.66, size * 0.42);
      ctx.lineTo(size * 0.3, -size * 0.12);
      ctx.lineTo(-size * 0.3, -size * 0.12);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, -size * 0.2);
      ctx.lineTo(0, -size * 0.7);
      ctx.stroke();
      break;

    case 'seedVault':
      // A sprout in a ring.
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.66, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, size * 0.38);
      ctx.lineTo(0, -size * 0.1);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(-size * 0.2, -size * 0.2, size * 0.22, -0.4, Math.PI * 0.9);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(size * 0.2, -size * 0.2, size * 0.22, Math.PI * 0.1, Math.PI * 1.4);
      ctx.stroke();
      break;

    case 'beacon':
      // A pulse tower with radiating arcs.
      ctx.beginPath();
      ctx.moveTo(0, size * 0.6);
      ctx.lineTo(0, -size * 0.3);
      ctx.stroke();
      for (let i = 1; i <= 2; i++) {
        ctx.beginPath();
        ctx.arc(0, -size * 0.3, size * 0.26 * i, Math.PI * 1.15, Math.PI * 1.85);
        ctx.stroke();
      }
      break;
  }
  ctx.restore();
}

/** Megastructure emblem. `timeMs` drives slow rotation and pulsing. */
export function drawMegastructure(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  kind: MegastructureKind,
  seat: PlayerId,
  timeMs: number,
): void {
  const color = seatColor(seat);
  const t = timeMs / 1000;

  ctx.save();
  ctx.translate(x, y);
  ctx.globalCompositeOperation = 'lighter';

  switch (kind) {
    case 'ringworld': {
      // An inclined ring; the near half is drawn over the planet, the far half behind it
      // is implied by a lower alpha.
      ctx.save();
      ctx.rotate(-0.5 + Math.sin(t * 0.08) * 0.04);
      for (const [alpha, width] of [[0.28, 0.05], [0.85, 0.085]] as const) {
        ctx.beginPath();
        ctx.ellipse(0, 0, size * 0.86, size * 0.3, 0, 0, Math.PI * 2);
        ctx.strokeStyle = withAlpha(color, alpha);
        ctx.lineWidth = size * width;
        ctx.shadowColor = withAlpha(color, 0.8);
        ctx.shadowBlur = size * 0.3;
        ctx.stroke();
      }
      ctx.restore();
      break;
    }

    case 'dysonSwarm': {
      // Orbiting collector panels.
      const panels = 12;
      for (let i = 0; i < panels; i++) {
        const angle = (i / panels) * Math.PI * 2 + t * 0.25;
        const rx = size * 0.78;
        const ry = size * 0.32;
        const px = Math.cos(angle) * rx;
        const py = Math.sin(angle) * ry;
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(angle);
        ctx.fillStyle = withAlpha(color, 0.55 + 0.4 * Math.sin(angle + t));
        ctx.fillRect(-size * 0.07, -size * 0.035, size * 0.14, size * 0.07);
        ctx.restore();
      }
      break;
    }

    case 'gateNexus': {
      // A rotating torus with an energy throat.
      ctx.save();
      ctx.rotate(t * 0.4);
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.6, 0, Math.PI * 2);
      ctx.strokeStyle = withAlpha(color, 0.9);
      ctx.lineWidth = size * 0.1;
      ctx.setLineDash([size * 0.28, size * 0.16]);
      ctx.shadowColor = withAlpha(color, 0.9);
      ctx.shadowBlur = size * 0.42;
      ctx.stroke();
      ctx.restore();

      const throat = ctx.createRadialGradient(0, 0, 0, 0, 0, size * 0.5);
      const pulse = 0.4 + 0.35 * Math.sin(t * 2.1);
      throat.addColorStop(0, withAlpha('#ffffff', pulse));
      throat.addColorStop(0.5, withAlpha(color, pulse * 0.6));
      throat.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = throat;
      ctx.fillRect(-size, -size, size * 2, size * 2);
      break;
    }

    case 'arkFoundry': {
      // Scaffold with a pulsing forge glow at the base.
      ctx.strokeStyle = withAlpha(color, 0.85);
      ctx.lineWidth = size * 0.055;
      ctx.shadowColor = withAlpha(color, 0.7);
      ctx.shadowBlur = size * 0.25;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(i * size * 0.3, -size * 0.6);
        ctx.lineTo(i * size * 0.3, size * 0.6);
        ctx.stroke();
      }
      for (let i = -1; i <= 1; i++) {
        ctx.beginPath();
        ctx.moveTo(-size * 0.62, i * size * 0.4);
        ctx.lineTo(size * 0.62, i * size * 0.4);
        ctx.stroke();
      }
      const forge = 0.35 + 0.4 * Math.abs(Math.sin(t * 1.4));
      const glow = ctx.createRadialGradient(0, size * 0.5, 0, 0, size * 0.5, size * 0.7);
      glow.addColorStop(0, `rgba(255, 170, 80, ${forge})`);
      glow.addColorStop(1, 'rgba(255, 120, 40, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(-size, -size * 0.2, size * 2, size * 1.4);
      break;
    }
  }
  ctx.restore();
}

/** Seat shape marker — colour-blind redundancy for ownership. */
export function drawSeatMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  seat: PlayerId,
  filled = true,
): void {
  const color = seatColor(seat);
  const shape = SEAT_SHAPE[seat] ?? 'circle';

  ctx.save();
  ctx.beginPath();
  switch (shape) {
    case 'circle':
      ctx.arc(x, y, size, 0, Math.PI * 2);
      break;
    case 'square':
      ctx.rect(x - size * 0.86, y - size * 0.86, size * 1.72, size * 1.72);
      break;
    case 'triangle':
      ctx.moveTo(x, y - size * 1.02);
      ctx.lineTo(x + size * 0.92, y + size * 0.7);
      ctx.lineTo(x - size * 0.92, y + size * 0.7);
      ctx.closePath();
      break;
    case 'diamond':
      ctx.moveTo(x, y - size * 1.1);
      ctx.lineTo(x + size * 0.94, y);
      ctx.lineTo(x, y + size * 1.1);
      ctx.lineTo(x - size * 0.94, y);
      ctx.closePath();
      break;
  }

  if (filled) {
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.lineWidth = Math.max(0.6, size * 0.2);
    ctx.stroke();
  } else {
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(0.9, size * 0.28);
    ctx.stroke();
  }
  ctx.restore();
}

/* ------------------------------------------------------------ public: readouts */

/** A row of resource pips, e.g. ◆◆▲ for a 2-Ore 1-Fuel yield. Centred on `x`. */
export function drawYieldPips(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  yields: Resources,
): void {
  const pips: { glyph: string; color: string }[] = [];
  for (const kind of ['ore', 'fuel', 'biomass', 'data'] as const) {
    for (let i = 0; i < yields[kind]; i++) {
      pips.push({ glyph: RESOURCE_GLYPH[kind], color: RESOURCE_INK[kind] });
    }
  }
  if (pips.length === 0) return;

  const step = size * 1.22;
  let cursor = x - ((pips.length - 1) * step) / 2;

  ctx.save();
  ctx.font = `700 ${size * 1.5}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // A dark outline keeps pips legible over both bright ice and dark volcanic bodies.
  ctx.strokeStyle = 'rgba(0,0,0,0.75)';
  ctx.lineWidth = Math.max(1.4, size * 0.5);
  for (const pip of pips) {
    ctx.strokeText(pip.glyph, cursor, y);
    ctx.fillStyle = pip.color;
    ctx.fillText(pip.glyph, cursor, y);
    cursor += step;
  }
  ctx.restore();
}

/** Hazard chevrons, 0..3. Stacked so severity reads at a glance. */
export function drawHazard(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  hazard: number,
): void {
  if (hazard <= 0) return;
  ctx.save();
  ctx.strokeStyle = INK.warn;
  ctx.lineWidth = Math.max(1, size * 0.34);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowColor = 'rgba(0,0,0,0.8)';
  ctx.shadowBlur = size * 0.6;

  for (let i = 0; i < Math.min(3, hazard); i++) {
    const cy = y + i * size * 0.78;
    ctx.beginPath();
    ctx.moveTo(x - size * 0.62, cy + size * 0.3);
    ctx.lineTo(x, cy - size * 0.3);
    ctx.lineTo(x + size * 0.62, cy + size * 0.3);
    ctx.stroke();
  }
  ctx.restore();
}

/** Habitability dots, 0..3 — filled for available, hollow for missing. */
export function drawHabitability(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  hab: number,
): void {
  const step = size * 2.9;
  let cursor = x - step;

  ctx.save();
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.arc(cursor, y, size, 0, Math.PI * 2);
    if (i < hab) {
      ctx.fillStyle = INK.good;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.6)';
      ctx.lineWidth = Math.max(0.5, size * 0.3);
      ctx.stroke();
    } else {
      ctx.strokeStyle = withAlpha(INK.textFaint, 0.85);
      ctx.lineWidth = Math.max(0.6, size * 0.36);
      ctx.stroke();
    }
    cursor += step;
  }
  ctx.restore();
}

/** Exposed for tests and for the asset generator's parity checks. */
export const SYSTEM_CLASS_KEYS = Object.keys(SYSTEM_CLASSES) as SystemClass[];
